import {UeThemeOptions} from './ueTheme.types';
import {Overrides} from '@material-ui/core/styles/overrides';

const inputFontSize = 16;
const shrinkFontSize = 13;
const inputPaddingPx = 16;
const inputPaddingTopPx = 20;

interface MuiLabsOverrides {
    MuiAutocomplete?: MuiLabAutocompleteOverrides;
}

export interface MuiLabAutocompleteOverrides {
    root: any;
    option: any;
    paper: any;
    groupLabel: any;
    inputRoot: any;
}

export const makeFormStyleOverrides = (
    themeOptions: UeThemeOptions
): Overrides | MuiLabsOverrides => {
    return {
        MuiAutocomplete: {
            root: {
                fontSize: inputFontSize
            },
            option: {
                fontSize: inputFontSize,
                paddingTop: '8px',
                paddingBottom: '8px'
            },
            paper: {
                'background-color': themeOptions.palette?.background?.default,
                border: '1px solid #ffffff',
                borderRadius: 0
            },
            groupLabel: {
                'background-color': themeOptions.palette?.background?.default
            },
            inputRoot: {
                '& [class*="MuiAutocomplete-tag"]': {
                    marginTop: `24px !important`,
                    height: '24px'
                }
            }
        },
        MuiButton: {
            root: {
                fontSize: inputFontSize,
                borderRadius: 0
            }
        },
        MuiCheckbox: {
            root: {
                padding: 0,
                marginLeft: '8px',
                '& [class*="MuiSvgIcon-root"]': {
                    height: '40px',
                    width: '40px',
                    fill: 'transparent!important',
                    color: `${themeOptions.palette?.accent?.sky}!important`,
                    stroke: '#F7FAFD!important',
                    padding: '6px'
                }
            },
            colorPrimary: {
                color: themeOptions.palette?.accent?.sky,
                '&:hover': {
                    fill: 'none',
                    backgroundColor: 'transparent!important',
                    background: 'transparent'
                },
                '&$checked': {
                    color: themeOptions.palette?.accent?.sky,
                    '&:hover': {
                        color: themeOptions.palette?.accent?.sky
                    },
                    '&.Mui-focusVisible': {
                        outline: `1px solid ${themeOptions.ue?.colors?.outline}`,
                        background: 'none',
                        '& [class*="MuiSvgIcon-root"]': {
                            fill: 'none',
                            background: 'transparent'
                        }
                    }
                },
                '&.Mui-focusVisible': {
                    outline: `1px solid ${themeOptions.ue?.colors?.outline}`,
                    background: 'none',
                    '& [class*="MuiSvgIcon-root"]': {
                        fill: 'none',
                        stroke: themeOptions.palette?.accent?.sky,
                        backgroundColor: 'transparent',
                        background: 'transparent'
                    }
                }
            },
            colorSecondary: {
                color: themeOptions.palette?.accent?.forest,
                '&:hover': {},
                '&$checked': {
                    color: themeOptions.palette?.accent?.forest,
                    '&:hover': {
                        color: themeOptions.palette?.accent?.forest
                    },
                    '&.Mui-focusVisible': {
                        outline: `1px solid ${themeOptions.ue?.colors?.outline}`,
                        background: 'none',
                        '& [class*="MuiSvgIcon-root"]': {
                            fill: 'none',
                            background: 'transparent'
                        }
                    }
                },
                '&.Mui-focusVisible': {
                    outline: `1px solid ${themeOptions.ue?.colors?.outline}`,
                    background: 'none',
                    '& [class*="MuiSvgIcon-root"]': {
                        fill: 'none',
                        background: 'transparent'
                    }
                }
            }
        },
        MuiChip: {
            root: {
                backgroundColor: themeOptions.palette?.background?.default,
                height: '32px',
                margin: '3px 3px 3px 15px!important',
                maxWidth: 'calc(100% - 6px)',
                borderRadius: '16px'
            }
        },
        MuiFormControlLabel: {
            label: {
                fontSize: inputFontSize,
                marginLeft: '0!important'
            }
        },
        MuiFormHelperText: {
            root: {
                fontSize: inputFontSize
            }
        },
        MuiFormLabel: {
            root: {
                fontSize: inputFontSize,
                color: themeOptions.palette?.text?.secondary
            },
            asterisk: {
                color: `${themeOptions.palette?.text?.secondary} !important`,
                '&$error': {
                    color: '#DE3341 !important'
                }
            }
        },
        MuiInputBase: {
            root: {
                background: themeOptions.palette?.background?.paper,
                color: themeOptions.palette?.text?.primary,
                fontFamily: themeOptions.fonts?.brutal.light,
                '&& $input': {
                    paddingTop: `${inputPaddingTopPx}px`,
                    paddingBottom: '0px',
                    paddingLeft: `${inputPaddingPx}px`,
                    paddingRight: `${inputPaddingPx}px`,
                    height: '60px'
                },
                '&& $inputMultiline': {
                    height: 'auto',
                    marginTop: `${inputPaddingTopPx}px`
                }
            },
            input: {
                '&:-webkit-autofill': {
                    background: themeOptions.palette?.background?.paper
                },
                background: themeOptions.palette?.background?.paper,
                fontSize: inputFontSize
            }
        },
        MuiInputLabel: {
            outlined: {
                transform: `translate(${inputPaddingPx}px, ${inputPaddingTopPx}px) scale(1) !important`,
                '&$shrink': {
                    transform: `translate(${inputPaddingPx}px, ${
                        inputPaddingPx / 2
                    }px) scale(0.875) !important`,
                    lineHeight: '16px'
                },
                textAlign: 'left'
            },
            shrink: {
                fontSize: `${shrinkFontSize}px`
            }
        },
        MuiMenu: {
            list: {
                margin: 0,
                padding: '8px 0',
                overflow: 'auto',
                listStyle: 'none',
                maxHeight: '40vh'
            },
            paper: {
                border: '1px solid #ffffff',
                fontFamily: 'Brutal, sans-serif',
                // transform: 'translateY(72px)',
                overflow: 'hidden',
                boxShadow: '0px 5px 10px rgb(0 0 0 / 35%)',
                borderRadius: 0,
                backgroundColor: themeOptions.palette?.background?.paper
            }
        },
        MuiMenuItem: {
            root: {
                color: themeOptions.palette?.text?.primary,
                fontSize: inputFontSize,
                borderRadius: 0,
                paddingTop: '8px',
                paddingBottom: '8px'
            },
            gutters: {
                minHeight: '48px'
            }
        },
        MuiOutlinedInput: {
            adornedEnd: {
                padding: '0!important',
                height: '72px'
            },
            root: {
                borderRadius: 0
            },
            notchedOutline: {
                borderColor: 'transparent'
            }
        },
        MuiSelect: {
            root: {
                fontFamily: 'Brutal, sans-serif',
                fontSize: inputFontSize,
                '&&&': {
                    height: '72px',
                    paddingTop: '30px',
                    minHeight: 'unset'
                }
            }
        },
        MuiLink: {
            root: {
                '&:active': {
                    textDecoration: 'none'
                }
            }
        }
    };
};
